import React, { useEffect, useState } from 'react';
import { Alert } from 'antd';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LoginPage from './components/Login';
import RegisterPage from './components/Register';
import Dashboard from './components/Dashboard';
import DoctorSettings from './components/DoctorSetting';
import ServicesManagement from './components/ServicesManagement';
import RevenueAnalytics from './components/RevenueAnalytics';
import { AuthProvider, useAuth } from './components/AuthContext';
import { PatientProvider } from './components/PatientContext';
import { DoctorProvider } from './components/DoctorContext';
import { OwnerProvider, useOwnerContext } from './components/OwnerContext';
import { NextVisitProvider } from './components/NextVisitContext';
import { ClinicProvider } from './components/ClinicContext';
import { InventoryProvider } from './components/InventoryContext';
import InventoryManagement from './components/InventoryManagement';
import ExternalServiceManagement from './components/ExternalServiceManagement';
import HistoryTemplateBuilder from './components/HistoryTemplateBuilder';
import VisitTypeSettings from './components/VisitTypeSettings';
import ReferralSourcesPage from './components/ReferralSourcesPage';
import PackagesManagement from './components/PackagesManagement';
import MedicinesManagement from './components/MedicinesManagement';
import AttendanceManagement from './components/AttendanceManagement';
import DoctorAttendanceManagement from './components/DoctorAttendanceManagement';
import DoctorAppointments from './components/DoctorAppointments';
import AppointmentReminderToast from './components/AppointmentReminderToast';
import OfflineStatus from './offline/OfflineStatus';
import OfflineWorkspaceStatus from './offline/OfflineWorkspaceStatus';
import AppointmentSlotsManagement from './components/AppointmentSlotsManagement';
import ExpensesManagement from './components/ExpensesManagement';
import InsuranceCompaniesManagement from './components/InsuranceCompaniesManagement';
import InsuranceReport from './components/InsuranceReport';
import OwnerDoctorManagement from './components/OwnerDoctorManagement';
import ClinicRevenueOverview from './components/ClinicRevenueOverview';
import PatientDataExport from './components/PatientDataExport';
import OwnerVisitReports from './components/OwnerVisitReports';
import { LanguageProvider, useLanguage } from './components/LanguageContext';

// Kept beside the protected routes so reminders float above every dashboard
// screen, including pages that do not render DashboardHeader.
const GlobalAppointmentReminder = () => {
  const { isLoggedIn } = useAuth();
  const { language } = useLanguage();
  const [doctorId, setDoctorId] = useState(() => localStorage.getItem('doctorId'));

  useEffect(() => {
    const syncDoctorId = () => setDoctorId(localStorage.getItem('doctorId'));
    window.addEventListener('doctorIdChanged', syncDoctorId);
    return () => window.removeEventListener('doctorIdChanged', syncDoctorId);
  }, []);

  if (!isLoggedIn()) return null;
  return <AppointmentReminderToast doctorId={doctorId} isRTL={language === 'ar'} />;
};

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isLoggedIn, authReady } = useAuth();
  const { isViewerOnly, loading } = useOwnerContext();
  const location = useLocation();

  if (!authReady) return null;

  if (!isLoggedIn()) {
    // Redirect to login with the intended destination
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Resolve the privilege before protected screens mount, so a viewer cannot
  // trigger a write during the short Firestore-loading window.
  if (loading) return null;

  // Viewer accounts are intentionally focused on their own patient records
  // and externally referred cases. Management screens remain unchanged for
  // every existing role.
  if (
    isViewerOnly &&
    !location.pathname.startsWith('/dashboard') &&
    location.pathname !== '/external-services'
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const ViewerOnlyNotice = () => {
  const { isLoggedIn } = useAuth();
  const { isViewerOnly, loading } = useOwnerContext();
  if (!isLoggedIn() || loading || !isViewerOnly) return null;

  return (
    <Alert
      type="info"
      showIcon
      message="Viewer-only account"
      description="You can view only your assigned data. Adding, editing, and deleting are disabled."
      style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 2000, maxWidth: 430 }}
    />
  );
};

// Protects analytics route: only owners (or legacy doctors) can see revenue & analytics
const AnalyticsRoute = ({ children }) => {
  const { canSeeRevenueAnalytics, loading } = useOwnerContext();
  if (loading) return null; // or a small spinner
  if (!canSeeRevenueAnalytics) return <Navigate to="/dashboard" replace />;
  return children;
};

const OwnerOnlyRoute = ({ children }) => {
  const { canManageOwnerFeatures, loading } = useOwnerContext();
  if (loading) return null;
  if (!canManageOwnerFeatures) return <Navigate to="/dashboard" replace />;
  return children;
};

const InsuranceRoute = ({ children }) => {
  const { canAccessInsurance, loading } = useOwnerContext();
  if (loading) return null;
  if (!canAccessInsurance) return <Navigate to="/dashboard" replace />;
  return children;
};

// Public route - redirects to dashboard if already logged in
const PublicRoute = ({ children }) => {
  const { isLoggedIn, authReady } = useAuth();

  if (!authReady) return null;

  if (isLoggedIn()) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <Router>
      <LanguageProvider>
        <AuthProvider>
          <DoctorProvider>
            <OwnerProvider>
            <ClinicProvider>
              <InventoryProvider>
                <NextVisitProvider>
                  <Routes>
                    <Route
                      path="/login"
                      element={
                        <PublicRoute>
                          <LoginPage />
                        </PublicRoute>
                      }
                    />
                    <Route
                      path="/register"
                      element={
                        <PublicRoute>
                          <RegisterPage />
                        </PublicRoute>
                      }
                    />
                    <Route
                      path="/dashboard/*"
                      element={
                        <ProtectedRoute>
                          <PatientProvider>
                            <Dashboard />
                          </PatientProvider>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <PatientProvider>
                            <DoctorSettings />
                          </PatientProvider>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/services"
                      element={
                        <ProtectedRoute>
                          <ServicesManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/analytics"
                      element={
                        <ProtectedRoute>
                          <AnalyticsRoute>
                            <RevenueAnalytics />
                          </AnalyticsRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/inventory"
                      element={
                        <ProtectedRoute>
                          <InventoryManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/external-services"
                      element={
                        <ProtectedRoute>
                          <ExternalServiceManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/history-template"
                      element={
                        <ProtectedRoute>
                          <HistoryTemplateBuilder />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/referral-sources"
                      element={
                        <ProtectedRoute>
                          <ReferralSourcesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/packages"
                      element={
                        <ProtectedRoute>
                          <PackagesManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/medicines"
                      element={
                        <ProtectedRoute>
                          <MedicinesManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/attendance"
                      element={
                        <ProtectedRoute>
                          <AttendanceManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/appointments"
                      element={
                        <ProtectedRoute>
                          <DoctorAppointments />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/doctor-attendance"
                      element={
                        <ProtectedRoute>
                          <DoctorAttendanceManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/timetable"
                      element={
                        <ProtectedRoute>
                          <AppointmentSlotsManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/expenses"
                      element={
                        <ProtectedRoute>
                          <AnalyticsRoute>
                            <ExpensesManagement />
                          </AnalyticsRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/insurance-companies"
                      element={
                        <ProtectedRoute>
                          <InsuranceRoute>
                            <InsuranceCompaniesManagement />
                          </InsuranceRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/insurance-report"
                      element={
                        <ProtectedRoute>
                          <InsuranceRoute>
                            <InsuranceReport />
                          </InsuranceRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/owner/doctors"
                      element={
                        <ProtectedRoute>
                          <AnalyticsRoute>
                            <OwnerOnlyRoute>
                              <OwnerDoctorManagement />
                            </OwnerOnlyRoute>
                          </AnalyticsRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/owner/revenue"
                      element={
                        <ProtectedRoute>
                          <AnalyticsRoute>
                            <ClinicRevenueOverview />
                          </AnalyticsRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/owner/patient-export"
                      element={
                        <ProtectedRoute>
                          <AnalyticsRoute>
                            <OwnerOnlyRoute>
                              <PatientDataExport />
                            </OwnerOnlyRoute>
                          </AnalyticsRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/owner/visit-reports"
                      element={
                        <ProtectedRoute>
                          <OwnerOnlyRoute>
                            <OwnerVisitReports />
                          </OwnerOnlyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/" element={<Navigate to="/dashboard" />} />
                    <Route path="*" element={<Navigate to="/login" />} />
                  </Routes>
                  <ViewerOnlyNotice />
                  <GlobalAppointmentReminder />
                  <OfflineStatus />
                  <OfflineWorkspaceStatus />
                </NextVisitProvider>
              </InventoryProvider>
            </ClinicProvider>
            </OwnerProvider>
          </DoctorProvider>
        </AuthProvider>
      </LanguageProvider>
    </Router>
  );
}

export default App;
